import { useEffect, useRef, useState } from 'react';
import { getContentPillars, getContentIdeas } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { Card } from '../../detail/Card';
import { SectionTitle } from '../../detail/SectionTitle';
import { useVideoPopup } from '../../detail/VideoPopup';

const EYE = '05 · Pipeline Builder';

const PASTEL_POOL = ['#D7C4A7', '#C4D6D4', '#C9D1B9', '#CBC3DC', '#D0C8B8', '#C8D4DC'];

const ARTICLE_TEMPLATE = [
  { name: 'Intro',      text: "A sentence that earns the click. A client moment, specific, dated." },
  { name: 'Principle',  text: "One rule you're willing to defend in front of a board." },
  { name: 'Lived',      text: 'The time it broke or held. Numbers where you have them.' },
  { name: 'Outcome',    text: 'What changed, measurably or visibly. Who noticed.' },
  { name: 'Conclusion', text: 'The sentence you want re-quoted. No call-to-action.' },
];

export function ContentDetail({ onClose }) {
  const [pillars, setPillars] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [selectedPillarId, setSelectedPillarId] = useState(null);
  const [editingPillar, setEditingPillar] = useState(null);
  const [addingPillar, setAddingPillar] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [addingIdea, setAddingIdea] = useState(false);
  const [error, setError] = useState(null);
  const vp = useVideoPopup();

  useEffect(() => {
    let cancelled = false;
    Promise.all([getContentPillars(), getContentIdeas()])
      .then(([p, i]) => {
        if (!cancelled) {
          setPillars(p);
          setIdeas(i);
          if (p.length > 0) setSelectedPillarId(p[0].id);
        }
      })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);

  // ── Pillar CRUD ──────────────────────────────────────────────────────────
  const commitPillar = (name) => {
    const id = `pil_${Date.now()}`;
    const color = PASTEL_POOL[pillars.length % PASTEL_POOL.length];
    setPillars(prev => [...prev, { id, name, color_hex: color }]);
    setAddingPillar(false);
  };

  const updatePillarName = (id, name) => {
    setPillars(prev => prev.map(p => p.id === id ? { ...p, name: name.trim() || p.name } : p));
    setEditingPillar(null);
  };

  const deletePillar = (id) => {
    setPillars(prev => {
      const next = prev.filter(p => p.id !== id);
      if (selectedPillarId === id) setSelectedPillarId(next[0]?.id ?? null);
      return next;
    });
    setIdeas(prev => prev.filter(i => i.pillar_id !== id));
    if (editingPillar === id) setEditingPillar(null);
  };

  // ── Idea CRUD ────────────────────────────────────────────────────────────
  const commitIdea = (title) => {
    if (!selectedPillarId) return;
    const id = `cid_${Date.now()}`;
    setIdeas(prev => [...prev, { id, pillar_id: selectedPillarId, title }]);
    setAddingIdea(false);
  };

  const updateIdeaTitle = (id, title) => {
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, title: title.trim() || i.title } : i));
    setEditingIdea(null);
  };

  const deleteIdea = (id) => {
    setIdeas(prev => prev.filter(i => i.id !== id));
    if (editingIdea === id) setEditingIdea(null);
  };

  // ── Guards ───────────────────────────────────────────────────────────────
  if (error) {
    return (
      <DetailShell eyebrow="05 · Pipeline Builder" title="Content Plan" onClose={onClose}>
        <div className="font-mono text-mono-tag uppercase text-danger">Could not load content data</div>
      </DetailShell>
    );
  }
  if (!pillars || !ideas) {
    return (
      <DetailShell eyebrow="05 · Pipeline Builder" title="Content Plan" onClose={onClose}>
        <div className="font-mono text-mono-tag uppercase text-ink-3">Loading…</div>
      </DetailShell>
    );
  }

  const selectedIdeas  = ideas.filter(i => i.pillar_id === selectedPillarId);
  const newPillarColor = PASTEL_POOL[pillars.length % PASTEL_POOL.length];
  const totalCols      = pillars.length + (addingPillar ? 1 : 0);

  return (
    <DetailShell eyebrow="05 · Pipeline Builder" title="Content Plan" onClose={onClose}>

      {/* ── Content pillars ─────────────────────────────────────────── */}
      <Card>
        <SectionTitle
          label="Content pillars"
          meta={`${pillars.length} PILLARS`}
          onPlay={vp.open}
          video={{ eyebrow: EYE, chapter: '01', duration: '02:18', title: 'Content pillars — the four topics you own', description: 'How to choose four content pillars that reflect your actual expertise and attract the right clients.' }}
        />
        {totalCols > 0 && (
          <div
            className="grid gap-2.5"
            style={{ gridTemplateColumns: `repeat(${totalCols}, 1fr)` }}
          >
            {pillars.map((p) => {
              const count = ideas.filter(i => i.pillar_id === p.id).length;
              return (
                <div
                  key={p.id}
                  className="relative border border-ink p-3 group"
                  style={{ background: p.color_hex }}
                >
                  <button
                    className="absolute top-1.5 right-1.5 font-mono text-[11px] leading-none text-ink-3 hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => deletePillar(p.id)}
                    aria-label={`Delete ${p.name}`}
                  >
                    ×
                  </button>
                  {editingPillar === p.id ? (
                    <InlineNameForm
                      defaultValue={p.name}
                      inputClassName="font-serif text-[18px] leading-[1.15] bg-transparent border-b border-ink w-full outline-none pr-4"
                      saveLabel="Save"
                      onSave={(name) => updatePillarName(p.id, name)}
                      onCancel={() => setEditingPillar(null)}
                    />
                  ) : (
                    <>
                      <div
                        className="font-serif text-[18px] leading-[1.15] cursor-pointer pr-4"
                        onClick={() => { setAddingPillar(false); setEditingPillar(p.id); }}
                      >
                        {p.name}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.06em] mt-2 text-ink-3">
                        {count} IDEAS
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            {addingPillar && (
              <div className="relative border border-ink p-3" style={{ background: newPillarColor }}>
                <InlineNameForm
                  defaultValue=""
                  inputClassName="font-serif text-[18px] leading-[1.15] bg-transparent border-b border-ink w-full outline-none"
                  placeholder="Pillar name"
                  saveLabel="Add"
                  onSave={commitPillar}
                  onCancel={() => setAddingPillar(false)}
                />
              </div>
            )}
          </div>
        )}
        {!addingPillar && (
          <button
            className="w-full font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 border border-dashed border-ink-3 px-3.5 py-1.5 cursor-pointer mt-2.5"
            onClick={() => { setEditingPillar(null); setAddingPillar(true); }}
          >
            + Add pillar
          </button>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4">

        {/* ── Things people should know ──────────────────────────────── */}
        <Card className="content-start">
          <SectionTitle
            label="Things people should know"
            meta={`${selectedIdeas.length} OF 10`}
            onPlay={vp.open}
            video={{ eyebrow: EYE, chapter: '02', duration: '03:04', title: 'Things people should know — writing ideas that earn the click', description: 'How to frame each idea as a specific, defensible insight rather than a generic tip.' }}
          />

          {/* Pillar selector */}
          {pillars.length > 1 && (
            <div className="flex gap-1 flex-wrap">
              {pillars.map((p) => (
                <button
                  key={p.id}
                  className={`font-mono text-[9px] uppercase tracking-[0.06em] px-2 py-0.5 border transition-colors ${
                    selectedPillarId === p.id
                      ? 'border-ink bg-ink text-paper'
                      : 'border-ink-3 text-ink-3 hover:border-ink hover:text-ink'
                  }`}
                  onClick={() => setSelectedPillarId(p.id)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}

          <ol className="m-0 pl-5 grid gap-1.5">
            {selectedIdeas.map((idea) => (
              <li key={idea.id}>
                {editingIdea === idea.id ? (
                  <InlineNameForm
                    defaultValue={idea.title}
                    inputClassName="w-full bg-transparent border-b border-ink outline-none text-[13px]"
                    saveLabel="Save"
                    onSave={(title) => updateIdeaTitle(idea.id, title)}
                    onCancel={() => setEditingIdea(null)}
                  />
                ) : (
                  <div className="group flex items-baseline gap-1.5">
                    <span
                      className="flex-1 cursor-pointer text-[13px]"
                      onClick={() => { setAddingIdea(false); setEditingIdea(idea.id); }}
                    >
                      {idea.title}
                    </span>
                    <button
                      className="font-mono text-[11px] leading-none text-ink-3 hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={() => deleteIdea(idea.id)}
                      aria-label="Delete idea"
                    >
                      ×
                    </button>
                  </div>
                )}
              </li>
            ))}
            {addingIdea && (
              <li>
                <InlineNameForm
                  defaultValue=""
                  inputClassName="w-full bg-transparent border-b border-ink outline-none text-[13px]"
                  placeholder="New idea"
                  saveLabel="Add"
                  onSave={commitIdea}
                  onCancel={() => setAddingIdea(false)}
                />
              </li>
            )}
          </ol>

          {!addingIdea && (
            <button
              className="w-full font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 border border-dashed border-ink-3 px-3.5 py-1.5 cursor-pointer mt-1"
              onClick={() => { setEditingIdea(null); setAddingIdea(true); }}
            >
              + Add idea
            </button>
          )}
        </Card>

        {/* ── Article template ───────────────────────────────────────── */}
        <Card className="content-start">
          <SectionTitle
            label="Article template"
            meta="INTRO → PRINCIPLE → LIVED → OUTCOME → CONCLUSION"
            onPlay={vp.open}
            video={{ eyebrow: EYE, chapter: '03', duration: '02:44', title: 'Article template — the five-section structure', description: 'Walk through each section of the article template and why the order earns trust before asking for attention.' }}
          />
          <div className="grid gap-2">
            {ARTICLE_TEMPLATE.map((r) => (
              <div key={r.name} className="px-2.5 py-1" style={{ borderLeft: '2px solid var(--ink)' }}>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
                  {r.name}
                </div>
                <div className="text-[13px]">{r.text}</div>
              </div>
            ))}
          </div>
        </Card>

      </div>

      {vp.render()}
    </DetailShell>
  );
}

function InlineNameForm({ defaultValue = '', inputClassName = '', placeholder = '', saveLabel = 'Save', onSave, onCancel }) {
  const [value, setValue] = useState(defaultValue);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed) onSave(trimmed);
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-1.5">
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className={inputClassName}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
      />
      <div className="flex gap-2 mt-0.5">
        <button
          type="submit"
          className="font-mono text-[10px] uppercase tracking-[0.08em] bg-ink text-paper border border-ink px-3 py-1.5 cursor-pointer"
        >
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 border border-hairline px-3 py-1.5 cursor-pointer"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default ContentDetail;
